// Managed contract detail page — header (title, status badge, back/edit/change
// status) plus tabbed content below. Tabs, in editorial order: Details,
// Deliverables, Milestones, Meetings, KPIs, Issues, Variations & SOWs,
// Insurances, Finance, Reports. Order, slugs, labels, and icons come from the
// shared tab registry (`../../components/manage/tabs/tabRegistry`).
//
// Tab state is synced to the URL as `?tab=<slug>&item=<itemId>`. Most tab
// slugs map 1:1 to a `TabLevelType` (the backend tracking-type discriminator).
// `'variations-sows'` is a frontend-only UI slug aggregating the `'sow'`
// and `'variation'` tracking types under one register.
//
// Access & People used to be its own tab; it now lives in a slide-over
// drawer launched from the People tile on the Details tab so the surface
// stays one click away without crowding the tab bar. Legacy
// `?tab=people-access` bookmarks redirect to Details with the drawer open.

import { useMemo, useCallback, useEffect } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router";
import { ArrowLeft } from "lucide-react";
import { Button, LoadingSpinner, Alert } from "../../components/common";
import { StatusPillButton } from "../../components/manage/contract-overview/StatusPillButton";
import ContractDetailsTab from "../../components/manage/ContractDetailsTab";
import TrackingTabPlaceholder from "../../components/manage/tracking/TrackingTabPlaceholder";
import InsuranceTab from "../../components/manage/tracking/insurance/InsuranceTab";
import DeliverableTab from "../../components/manage/tracking/deliverable/DeliverableTab";
import MilestoneTab from "../../components/manage/tracking/milestone/MilestoneTab";
import MeetingTab from "../../components/manage/tracking/meeting/MeetingTab";
import KpiTab from "../../components/manage/tracking/kpi/KpiTab";
import VariationSowTab from "../../components/manage/tracking/variations-sows/VariationSowTab";
import IssueTab from "../../components/manage/tracking/issue/IssueTab";
import FinanceTab from "../../components/manage/finance/FinanceTab";
import ReportsTab from "../../components/manage/report/ReportsTab";
import {
  useManagedContract,
  useUpdateManagedContract,
} from "../../hooks/useManagedContracts";
import { useContractTrackingCounts } from "../../hooks/useTracking";
import { clearTabScopedParams } from "../../components/manage/tracking/shared/trackingUrlParams";
import ContractTabBar from "../../components/manage/tabs/ContractTabBar";
import {
  getTabDef,
  isTabSlug,
  type TabSlug,
} from "../../components/manage/tabs/tabRegistry";
import type {
  ManagedContractStatus,
  TabLevelType,
} from "../../types";

export default function ManagedContractDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  // Legacy ?tab=people-access bookmarks land on Details with the drawer
  // open; the actual drawer-open state is owned by ContractDetailsTab via
  // its ?openPeopleAccess query param.
  const legacyPeopleAccess = tabParam === "people-access";
  const activeTab: TabSlug = isTabSlug(tabParam) ? tabParam : "details";

  useEffect(() => {
    if (!legacyPeopleAccess) return;
    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    next.set("openPeopleAccess", "1");
    setSearchParams(next, { replace: true });
  }, [legacyPeopleAccess, searchParams, setSearchParams]);

  const { data: contract, isLoading, error } = useManagedContract(id || "");
  const updateMutation = useUpdateManagedContract();
  const { data: counts } = useContractTrackingCounts(id || "");

  const badgeCounts = useMemo<Partial<Record<TabSlug, number | undefined>>>(
    () => {
      // The combined Variations & SOWs tab sums both backend types so the
      // badge stays a single navigational target. The variation bucket
      // is `undefined` for contracts that have no variations yet.
      // Suppress the badge entirely when both buckets are absent — matches
      // the parity with single-type tabs where an undefined count yields
      // no badge.
      const sow = counts?.sow?.total;
      const variation = counts?.variation?.total;
      const combined =
        sow === undefined && variation === undefined
          ? undefined
          : (sow ?? 0) + (variation ?? 0);
      // Reports carries no badge at all: it owns no records of its own, so
      // there is no count that would mean anything there.
      // The finance bucket counts invoices awaiting review, not total
      // invoices — a permanent "0" badge would read as "no invoices", so
      // zero is suppressed and the badge only appears when action is needed.
      const financeAwaitingReview = counts?.finance?.total;
      return {
        insurance: counts?.insurance?.total,
        deliverable: counts?.deliverable?.total,
        milestone: counts?.milestone?.total,
        meeting: counts?.meeting?.total,
        kpi: counts?.kpi?.total,
        "variations-sows": combined,
        issue: counts?.issue?.total,
        finance: financeAwaitingReview || undefined,
      };
    },
    [counts],
  );

  // Which tabs carry an attention flag (drives the dot on the More button when
  // the tab is collapsed into the overflow row). The combined Variations & SOWs
  // tab needs attention if EITHER backend bucket flags it. Only the per-contract
  // counts endpoint emits hasAttention; absence means no dot.
  const attention = useMemo<Partial<Record<TabSlug, boolean>>>(
    () => ({
      insurance: counts?.insurance?.hasAttention,
      deliverable: counts?.deliverable?.hasAttention,
      milestone: counts?.milestone?.hasAttention,
      meeting: counts?.meeting?.hasAttention,
      kpi: counts?.kpi?.hasAttention,
      "variations-sows":
        counts?.sow?.hasAttention || counts?.variation?.hasAttention,
      issue: counts?.issue?.hasAttention,
      finance: counts?.finance?.hasAttention,
    }),
    [counts],
  );

  const handleTabChange = useCallback(
    (tab: TabSlug, options?: { replace?: boolean }) => {
      const next = new URLSearchParams(searchParams);
      if (tab === "details") {
        next.delete("tab");
      } else {
        next.set("tab", tab);
      }
      // Tab-scoped params (search, filters, sort, pagination, view, scope)
      // must not carry from one tab to another — each tab starts fresh.
      // `item` and cross-tab create params (action, sowId) are also cleared.
      clearTabScopedParams(next);
      next.delete("item");
      next.delete("action");
      next.delete("sowId");
      // Click activation pushes, so browser Back navigates between tabs
      // rather than exiting the detail page outright. Keyboard roving asks
      // for replace, so arrow-keying across tabs doesn't flood history with
      // one entry per traversed tab.
      setSearchParams(next, options?.replace ? { replace: true } : undefined);
    },
    [searchParams, setSearchParams],
  );

  const handleStatusChange = async (
    newStatus: ManagedContractStatus,
    reason?: string,
  ) => {
    if (!contract) return;
    try {
      await updateMutation.mutateAsync({
        id: contract.id,
        input: {
          status: newStatus,
          version: contract.version,
          reason,
        },
      });
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate("/manage/contracts")}
        >
          Back
        </Button>
        <Alert variant="error">
          {error ? (error as Error).message : "Contract not found."}
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={() => navigate("/manage/contracts")}
        className="inline-flex items-center gap-1 text-caption text-slate-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-400 focus:outline-hidden focus:ring-2 focus:ring-brand-400 rounded-sm"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        Back to register
      </button>
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-h1 text-slate-900 dark:text-slate-50">
              {contract.title}
            </h1>
            <StatusPillButton
              status={contract.status}
              onChange={handleStatusChange}
              isLoading={updateMutation.isPending}
            />
          </div>
          <p className="font-mono text-body-sm text-slate-500 dark:text-slate-400">
            {contract.contractNumber}
          </p>
        </div>
      </div>

      <ContractTabBar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        badgeCounts={badgeCounts}
        attention={attention}
      />

      {activeTab === "details" && (
        <div
          role="tabpanel"
          id="details-panel"
          aria-labelledby="details-panel-tab"
        >
          <ContractDetailsTab contract={contract} />
        </div>
      )}
      {activeTab !== "details" && (
        <div
          role="tabpanel"
          id={`${activeTab}-panel`}
          aria-labelledby={`${activeTab}-panel-tab`}
        >
          {activeTab === "insurance" && id ? (
            <InsuranceTab
              contractId={id}
              itemIdFromUrl={searchParams.get("item")}
            />
          ) : activeTab === "deliverable" && id ? (
            <DeliverableTab
              contractId={id}
              itemIdFromUrl={searchParams.get("item")}
              onNavigateToMilestone={(milestoneId) => {
                const next = new URLSearchParams(searchParams);
                clearTabScopedParams(next);
                next.set("tab", "milestone");
                next.set("item", milestoneId);
                setSearchParams(next);
              }}
            />
          ) : activeTab === "milestone" && id ? (
            <MilestoneTab
              contractId={id}
              contractValue={contract?.value}
              gstApplies={contract?.gstApplies}
              contractStartDate={contract?.startDate}
              contractEndDate={contract?.endDate}
              itemIdFromUrl={searchParams.get("item")}
              onNavigateToDeliverable={(deliverableId) => {
                const next = new URLSearchParams(searchParams);
                clearTabScopedParams(next);
                next.set("tab", "deliverable");
                next.set("item", deliverableId);
                setSearchParams(next);
              }}
              onNavigateToSow={(sowId) => {
                const next = new URLSearchParams(searchParams);
                clearTabScopedParams(next);
                next.set("tab", "variations-sows");
                next.set("item", sowId);
                setSearchParams(next);
              }}
            />
          ) : activeTab === "meeting" && id ? (
            <MeetingTab
              contractId={id}
              itemIdFromUrl={searchParams.get("item")}
            />
          ) : activeTab === "kpi" && id ? (
            <KpiTab
              contractId={id}
              itemIdFromUrl={searchParams.get("item")}
            />
          ) : activeTab === "variations-sows" && id ? (
            <VariationSowTab
              contractId={id}
              itemIdFromUrl={searchParams.get("item")}
              onNavigateToDeliverable={(deliverableId) => {
                const next = new URLSearchParams(searchParams);
                clearTabScopedParams(next);
                next.set("tab", "deliverable");
                next.set("item", deliverableId);
                setSearchParams(next);
              }}
              onNavigateToMilestone={(milestoneId) => {
                const next = new URLSearchParams(searchParams);
                clearTabScopedParams(next);
                next.set("tab", "milestone");
                next.set("item", milestoneId);
                setSearchParams(next);
              }}
              onAddDeliverableToSow={(sowId) => {
                const next = new URLSearchParams(searchParams);
                clearTabScopedParams(next);
                next.set("tab", "deliverable");
                next.set("action", "create");
                next.set("scope", `sow:${sowId}`);
                next.delete("item");
                next.delete("sowId");
                setSearchParams(next);
              }}
              onAddMilestoneToSow={(sowId) => {
                const next = new URLSearchParams(searchParams);
                clearTabScopedParams(next);
                next.set("tab", "milestone");
                next.set("action", "create");
                next.set("scope", `sow:${sowId}`);
                next.delete("item");
                next.delete("sowId");
                setSearchParams(next);
              }}
            />
          ) : activeTab === "issue" && id ? (
            <IssueTab
              contractId={id}
              itemIdFromUrl={searchParams.get("item")}
              linkedTypeFromUrl={searchParams.get("linkedType")}
              linkedIdFromUrl={searchParams.get("linkedId")}
              onNavigateToLinkedItem={(targetType, targetId) => {
                // Map the issue-link target type to its tab slug and open the
                // item. kpi-measurement opens the kpi tab with the parent KPI
                // id (see IssueDetailDrawer comment for the limitation).
                const tabSlug =
                  targetType === "kpi-measurement" ? "kpi" : targetType;
                const next = new URLSearchParams(searchParams);
                clearTabScopedParams(next);
                next.set("tab", tabSlug);
                next.set("item", targetId);
                setSearchParams(next);
              }}
            />
          ) : activeTab === "finance" && id ? (
            <FinanceTab
              contractId={id}
              contract={contract}
              itemIdFromUrl={searchParams.get("item")}
            />
          ) : activeTab === "reports" && id ? (
            <ReportsTab contractId={id} />
          ) : activeTab !== "variations-sows" ? (
            <TrackingTabPlaceholder
              type={activeTab as TabLevelType}
              icon={getTabDef(activeTab).icon}
            />
          ) : null}
        </div>
      )}

    </div>
  );
}

